import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Link as LinkIcon, Warning as WarningIcon } from '@mui/icons-material';

interface CancelWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  vendorName: string;
}

export const CancelWarningDialog: React.FC<CancelWarningDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  vendorName,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: 2 },
      }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon color="warning" />
          <Typography fontWeight="bold">Create New Vendor?</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Clicking Cancel will create a new vendor entry in your Vendor Master
          with the name:
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          color="primary"
          sx={{ mt: 1, mb: 1 }}>
          "{vendorName}"
        </Typography>
        <Typography variant="body2" color="text.secondary">
          If this vendor already exists under a different name, consider mapping
          it to the existing vendor instead.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2 }}>
          Go Back
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          size="small"
          disabled={loading}
          sx={{ borderRadius: 2 }}>
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Create New Vendor'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MapConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  extractedVendorName: string;
  selectedVendorName: string;
}

export const MapConfirmationDialog: React.FC<MapConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  extractedVendorName,
  selectedVendorName,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: 2 },
      }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinkIcon color="primary" />
          <Typography fontWeight="bold">Confirm Vendor Mapping</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          You are about to map the extracted vendor name:
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          color="primary"
          sx={{ mt: 1, mb: 1 }}>
          "{extractedVendorName}"
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          to the existing vendor:
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          color="success.main"
          sx={{ mb: 1 }}>
          "{selectedVendorName}"
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Future invoices with this vendor name will automatically be linked to
          the selected vendor.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2 }}>
          Go Back
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          size="small"
          disabled={loading}
          sx={{ borderRadius: 2 }}>
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Confirm Mapping'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
